import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Euro, CheckCircle, Shield, Lightbulb, FileText, Users, Receipt, ArrowRight, ChevronDown, ChevronUp, Route, TrendingUp } from 'lucide-react';

// Import gotowych komponentów
import Trust from '../Trust';
import Newsletter from '../Newsletter';
import ContactForm from '../ContactForm';
import Stats from '../Stats';

// Dodanie importu czcionki Inter
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const PozyskiwanieDofinansowan = () => {
  const [activeFaq, setActiveFaq] = useState(null);

  const handleContactClick = () => {
    const contactSection = document.querySelector('#kontakt');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      {/* Hero Section - PRZEBUDOWANA SEKCJA */}
      <section className="relative pt-28 sm:pt-32 pb-16 lg:pt-48 lg:pb-32 overflow-hidden bg-black">
        <div className="absolute inset-0 opacity-10">
          {/* Dekoracyjne, animowane ikony w tle */}
          <div className="absolute top-1/4 left-[15%] animate-float">
            <FileText className="w-24 h-24 text-violet-600/40" />
          </div>
          <div className="absolute bottom-1/4 right-[15%] animate-float animation-delay-2000">
            <TrendingUp className="w-32 h-32 text-violet-600/40" />
          </div>
          <div className="absolute top-20 right-[40%] animate-float animation-delay-1000">
            <Lightbulb className="w-20 h-20 text-violet-600/40" />
          </div>
        </div>

        <div className="relative z-10 mx-auto text-center px-4">
            <h1 
              className="text-3xl sm:text-4xl md:text-6xl text-white mb-6 uppercase leading-tight"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}
            >
              Pozyskiwanie Dofinansowań
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-6 sm:mb-8 font-semibold max-w-3xl mx-auto">
              Skuteczne Wsparcie w Ubieganiu się o Fundusze Unijne i Krajowe
            </p>
            <p className="text-base sm:text-lg text-gray-300 mb-8 sm:mb-10 max-w-3xl mx-auto">
              <strong>Pozyskiwanie dofinansowań to nasza specjalność.</strong> Szynkowski & Company przeprowadzi Cię przez cały proces – od pomysłu, przez strategię, aż po finalne uzyskanie środków. Z nami Twoje projekty nabiorą realnych kształtów.
            </p>
            <div className="flex justify-center">
                <button 
                    onClick={handleContactClick} 
                    className="group flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-violet-600 to-violet-700 text-white font-semibold rounded-full transition-all duration-300 hover:from-violet-700 hover:to-violet-800 hover:scale-105 text-center"
                >
                    <span>Bezpłatna konsultacja</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
            </div>
        </div>
        <style>{`
          @keyframes float {
              0%, 100% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-20px) rotate(5deg); }
          }
          .animate-float {
              animation: float 8s ease-in-out infinite;
          }
          .animation-delay-1000 { animation-delay: 1s; }
          .animation-delay-2000 { animation-delay: 2s; }
        `}</style>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-24 relative bg-black">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-5xl text-white text-center mb-6 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
            Kompleksowe wsparcie na każdym etapie
          </h2>
          <p className="text-lg text-gray-300 text-center mb-16 max-w-3xl mx-auto">
            W Szynkowski & Company oferujemy pełne wsparcie w pozyskiwaniu środków, 
            minimalizując ryzyko i maksymalizując szanse na sukces.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Analiza kwalifikowalności", description: "Weryfikujemy, czy Twój projekt spełnia kryteria wybranego programu.", icon: <CheckCircle className="w-7 h-7" /> },
              { title: "Strategia i optymalizacja", description: "Doradzamy w zakresie optymalizacji projektu pod kątem kryteriów oceny.", icon: <Lightbulb className="w-7 h-7" /> },
              { title: "Przygotowanie dokumentacji", description: "Tworzymy kompletną i merytorycznie poprawną dokumentację aplikacyjną.", icon: <FileText className="w-7 h-7" /> },
              { title: "Monitoring i komunikacja", description: "Śledzimy proces oceny wniosku i prowadzimy komunikację z instytucjami.", icon: <Users className="w-7 h-7" /> },
              { title: "Wsparcie po uzyskaniu decyzji", description: "Pomagamy w formalnościach związanych z podpisaniem umowy o dofinansowanie.", icon: <Shield className="w-7 h-7" /> },
              { title: "Rozliczanie dotacji", description: "Zapewniamy wsparcie w prawidłowym rozliczeniu projektu, zgodnie z wymogami.", icon: <Receipt className="w-7 h-7" /> }
            ].map((step) => (
              <div key={step.title} className="backdrop-blur-md bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-violet-600/50 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-full flex items-center justify-center flex-shrink-0 text-violet-600">
                    {step.icon}
                  </div>
                  <div>
                    <h4 className="text-lg text-white font-bold mb-2">{step.title}</h4>
                    <p className="text-gray-400 text-sm">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-16 md:py-24 relative bg-black">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-5xl text-white text-center mb-10 md:mb-16 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
            Dlaczego warto nam zaufać?
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { title: "Unikalne połączenie kompetencji", description: "Zapewniamy skuteczność w pozyskiwaniu środków i bezpieczeństwo prawne projektów." },
              { title: "Gwarancja poufności", description: "Wszystkie informacje dotyczące Twojego przedsiębiorstwa są objęte tajemnicą adwokacką." },
              { title: "Innowacyjne podejście", description: "Współpracujemy z naukowcami i specjalistami, wspierając projekty o wysokim potencjale." },
              { title: "Optymalizacja procedur", description: "Przejmujemy ciężar formalności, pozwalając Ci skupić się na rozwoju Twojego biznesu." }
            ].map((benefit) => (
              <div key={benefit.title} className="backdrop-blur-md bg-white/5 rounded-2xl p-5 sm:p-8 border border-white/10 hover:border-violet-600/50 transition-all duration-300">
                <h3 className="text-xl font-bold text-white mb-4">{benefit.title}</h3>
                <p className="text-gray-300">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Aktualne Dotacje Section - NOWA SEKCJA 
      <section className="py-16 md:py-24 relative bg-black">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-5xl text-white mb-6 uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Aktualne Dotacje
            </h2>
            <p className="text-lg text-gray-300 mb-16 max-w-3xl mx-auto">
              Monitorujemy dostępne programy i nabory, aby informować Cię o najlepszych możliwościach finansowania dla Twojej firmy. Sprawdź, co jest teraz na topie.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="group backdrop-blur-md bg-white/5 rounded-2xl p-8 border border-white/10 hover:border-violet-600/50 hover:bg-white/10 transition-all duration-300 flex flex-col sm:flex-row items-center gap-8">
              <div className="w-20 h-20 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-full flex items-center justify-center flex-shrink-0 text-violet-600">
                <Route className="w-10 h-10" />
              </div>
              <div className="flex-grow text-center sm:text-left">
                <h3 className="text-2xl font-bold text-white mb-2">Ścieżka SMART (FENG)</h3>
                <p className="text-gray-300 mb-4">
                  Kluczowy program dla innowacyjnych projektów B+R. Modułowa konstrukcja pozwala elastycznie finansować badania, wdrożenia, cyfryzację i zazielenienie firmy.
                </p>
              </div>
              <Link
                to="/dotacje/sciezka-smart"
                className="flex-shrink-0 group/link inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white font-semibold rounded-full transition-all duration-300 group-hover:bg-violet-700 group-hover:border-violet-700"
              >
                <span>Dowiedz się więcej</span>
                <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>*/}
      
      <Trust />
      <Stats />
      
      {/* FAQ Section */}
      <section className="py-16 md:py-24 relative bg-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-5xl text-white text-center mb-8 md:mb-12 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
            Najczęściej zadawane pytania
          </h2>
          <div className="space-y-4">
            {[
              { question: "Jak długo trwa proces pozyskiwania dofinansowania?", answer: "Czas trwania procesu zależy od wielu czynników, m.in. specyfiki programu, terminów naboru i złożoności projektu. Zazwyczaj jest to od kilku do kilkunastu miesięcy." },
              { question: "Czy gwarantujecie uzyskanie dofinansowania?", answer: "Żadna firma nie może tego zagwarantować. Gwarantujemy jednak nasze pełne zaangażowanie, wiedzę i maksymalne zwiększenie szans projektu na sukces." },
              { question: "Jakie dokumenty są potrzebne do przygotowania wniosku?", answer: "Wymagane dokumenty różnią się w zależności od programu. Przedstawimy Ci szczegółową listę po analizie Twojego projektu i potrzeb." }
            ].map((faq, index) => (
              <div key={index} className="backdrop-blur-md bg-white/5 rounded-xl border border-white/10 overflow-hidden transition-all duration-300 hover:border-violet-600/30">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center group"
                >
                  <span className="font-semibold text-lg text-white">{faq.question}</span>
                  {activeFaq === index ? 
                    <ChevronUp className="w-5 h-5 text-violet-600 flex-shrink-0" /> :
                    <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-violet-600 transition-colors flex-shrink-0" />
                  }
                </button>
                <div className={`transition-all duration-300 ${activeFaq === index ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="overflow-hidden px-6 pb-4">
                    <p className="text-gray-300">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Newsletter />
      <div id="kontakt">
        <ContactForm />
      </div>

      <style>{`
        .animation-delay-2000 { animation-delay: 2s; }
        .node {
            width: 6rem; height: 6rem;
            border-radius: 9999px;
            display: flex; align-items: center; justify-content: center;
            background: radial-gradient(circle, rgba(249, 115, 22, 0.15) 0%, rgba(249, 115, 22, 0) 70%);
            border: 1px solid rgba(249, 115, 22, 0.2);
            color: #fb923c; /* text-violet-500 */
            animation: pulse-glow 4s infinite ease-in-out;
            animation-delay: var(--delay);
        }
        .connector {
            position: absolute;
            height: 1px;
            background: linear-gradient(90deg, transparent, rgba(249, 115, 22, 0.4), transparent);
            animation: flow 5s infinite linear;
        }
        @keyframes pulse-glow {
            0%, 100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.2); }
            50% { transform: scale(1.05); box-shadow: 0 0 20px 10px rgba(249, 115, 22, 0); }
        }
        @keyframes flow {
            0% { transform-origin: left; transform: scaleX(0); }
            50% { transform-origin: left; transform: scaleX(1); }
            50.001% { transform-origin: right; transform: scaleX(1); }
            100% { transform-origin: right; transform: scaleX(0); }
        }
        .max-h-96 { max-height: 24rem; }
      `}</style>
    </div>
  );
};

export default PozyskiwanieDofinansowan;